import { forwardRef } from 'react';
import { clsx } from 'clsx';
import { CardProps } from '@/shared/types/common.types';

const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      children,
      title,
      subtitle,
      image,
      imageAlt,
      header,
      footer,
      actions,
      variant = 'default',
      clickable = false,
      loading = false,
      className,
      onClick,
      ...props
    },
    ref
  ) => {
    const baseClasses = 'card';
    
    const variantClasses = {
      default: '',
      elevated: 'shadow-medium',
      outlined: 'border-2',
      filled: 'bg-neutral-100',
    };

    const classes = clsx(
      baseClasses,
      variantClasses[variant],
      {
        'card-interactive cursor-pointer': clickable && !loading,
        'opacity-75': loading,
      },
      className
    );

    const content = (
      <>
        {image && (
          <div className="aspect-w-16 aspect-h-9 rounded-t-xl overflow-hidden">
            <img
              src={image}
              alt={imageAlt || title || 'Card image'}
              className="w-full h-full object-cover"
            />
          </div>
        )}
        
        {header && (
          <div className="px-6 py-4 border-b border-neutral-200">
            {header}
          </div>
        )}
        
        {(title || subtitle) && (
          <div className="px-6 py-4 border-b border-neutral-200">
            {title && (
              <h3 className="text-lg font-semibold text-neutral-900 mb-1">
                {title}
              </h3>
            )}
            {subtitle && (
              <p className="text-sm text-neutral-600">
                {subtitle}
              </p>
            )}
          </div>
        )}
        
        <div className="px-6 py-4">
          {loading ? (
            <div className="space-y-3">
              <div className="skeleton h-4 w-3/4"></div>
              <div className="skeleton h-4 w-1/2"></div>
              <div className="skeleton h-4 w-2/3"></div>
            </div>
          ) : (
            children
          )}
        </div>
        
        {(footer || actions) && (
          <div className="px-6 py-4 border-t border-neutral-200 bg-neutral-50 rounded-b-xl">
            {footer && <div className="mb-4 last:mb-0">{footer}</div>}
            {actions && (
              <div className="flex items-center justify-end space-x-3">
                {actions}
              </div>
            )}
          </div>
        )}
      </>
    );

    if (clickable && onClick) {
      return (
        <button
          ref={ref}
          className={clsx(classes, 'text-left w-full')}
          onClick={onClick}
          disabled={loading}
          {...props}
        >
          {content}
        </button>
      );
    }

    return (
      <div ref={ref} className={classes} {...props}>
        {content}
      </div>
    );
  }
);

Card.displayName = 'Card';

export { Card };